import React from "react";
import { ArrowRight, ShieldCheck, Clock, ChartPieSlice, Scales } from "@phosphor-icons/react";
import Disclosure from "./Disclosure";

const CATS = [
  ["Cash Flow & Savings", 20, "How much of what you earn is put to work."],
  ["Tax Efficiency", 20, "How your savings are spread across tax categories."],
  ["Insurance & Protection", 15, "Whether protection matches your responsibilities."],
  ["Net Worth & Resilience", 20, "What you own versus owe — and the trend."],
  ["Retirement Readiness", 25, "Whether your pace fits your timeline."],
];

export default function ClarityIntro({ onStart, quickCount, hasSaved, onResume }) {
  return (
    <div data-testid="clarity-intro" className="space-y-24">
      <section className="grid lg:grid-cols-12 gap-12 items-end">
        <div className="lg:col-span-8 space-y-8">
          <p className="eyebrow rise"><span className="gold-divider" />The Exodus Financial Clarity Score</p>
          <h1 data-testid="clarity-headline" className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1] rise rise-1">
            Understand where you stand financially — and identify opportunities to <span className="gold-text italic">strengthen your future.</span>
          </h1>
          <p className="text-base lg:text-lg text-exodus-muted max-w-2xl leading-relaxed rise rise-2">
            Answer a series of straightforward questions about your income, savings, protection, tax exposure, and retirement goals. Receive an educational assessment of your current financial position and identify the areas that may deserve further attention.
          </p>
          <div className="flex flex-wrap items-center gap-4 rise rise-3">
            <button data-testid="clarity-start-btn" onClick={onStart} className="btn-gold">Start my Clarity Score <ArrowRight size={18} weight="bold" /></button>
            {hasSaved && <button data-testid="clarity-resume-btn" onClick={onResume} className="btn-ghost">View my last report</button>}
          </div>
          <div className="flex flex-wrap gap-6 text-xs text-exodus-muted rise rise-4">
            <span className="flex items-center gap-2"><Clock size={14} className="text-exodus-gold" /> {quickCount} questions · ranges, not exact balances</span>
            <span className="flex items-center gap-2"><ShieldCheck size={14} className="text-exodus-gold" /> No SSN, account numbers, or statements</span>
            <span className="flex items-center gap-2"><Scales size={14} className="text-exodus-gold" /> "I don't know" never lowers your score</span>
          </div>
        </div>
        <div className="lg:col-span-4 card-surface p-6 rise rise-2">
          <p className="eyebrow flex items-center gap-2 mb-5"><ChartPieSlice size={14} /> What's measured</p>
          <ul className="space-y-4">
            {CATS.map(([n, w, d]) => (
              <li key={n} className="flex justify-between gap-4 border-b border-exodus-border pb-3 last:border-0">
                <div><p className="text-sm text-exodus-text">{n}</p><p className="text-xs text-exodus-muted">{d}</p></div>
                <span className="font-display text-2xl gold-text">{w}%</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="grid md:grid-cols-3 gap-px bg-exodus-border border border-exodus-border">
        {[
          ["01", "Answer honestly", "Use ranges. Skip anything you'd rather not share — unknown answers lower confidence, not your score."],
          ["02", "See your score", "A transparent 0–100 score with a breakdown by category, strengths, and gaps — calculated by fixed, documented rules."],
          ["03", "Choose your next step", "Keep learning on your own, download your report, or book a free, no-obligation conversation."],
        ].map(([n, t, d]) => (
          <div key={n} className="bg-exodus-bg p-8">
            <p className="font-display text-4xl gold-text">{n}</p>
            <p className="font-display text-2xl mt-3">{t}</p>
            <p className="text-sm text-exodus-muted mt-2">{d}</p>
          </div>
        ))}
      </section>
      <Disclosure />
    </div>
  );
}
